import type { ProductImageEntry } from "./types";

/** Native help images; no marketing outcome claims or persisted changes. */
export const RESOURCE_NEXT_DETAIL_CATALOGUE: readonly ProductImageEntry[] = [
  {
    id: "onboarding.clients-list",
    module: "Klienter i startguiden",
    area: "standing-data",
    opens: "/start/clients",
    setup: ["Öppna den befintliga vyn; spara eller skicka inga ändringar"],
    scenario: "S5",
    visual: {
      variant: 0,
    },
    text: {
      sv: {
        alt: "Klienter i startguiden",
        shows:
          "Listan visar befintliga klienter i startguidens klient- och besökssteg.",
        proves:
          "Hjälpbilden visar var dessa uppgifter granskas i den verkliga vyn. Inga ändringar har sparats.",
      },
      en: {
        alt: "Clients in the setup guide",
        shows:
          "The list shows existing clients in the setup guide’s client and visit step.",
        proves:
          "This help image shows where these details are reviewed in the actual view. No changes have been saved.",
      },
    },
    use: ["help"],
  },
  {
    id: "onboarding.client-readiness",
    module: "Klientens datakvalitet i startguiden",
    area: "standing-data",
    opens: "/start/clients/:clientId",
    setup: ["Öppna den befintliga vyn; spara eller skicka inga ändringar"],
    scenario: "S5",
    visual: {
      variant: 0,
    },
    text: {
      sv: {
        alt: "Klientens datakvalitet i startguiden",
        shows:
          "Klientens status, datakvalitet och besöksunderlag visas tillsammans.",
        proves:
          "Hjälpbilden visar var dessa uppgifter granskas i den verkliga vyn. Inga ändringar har sparats.",
      },
      en: {
        alt: "Client data quality in the setup guide",
        shows:
          "The client’s readiness, data quality and visit foundation appear together.",
        proves:
          "This help image shows where these details are reviewed in the actual view. No changes have been saved.",
      },
    },
    use: ["help"],
  },
  {
    id: "onboarding.employees-list",
    module: "Medarbetare i startguiden",
    area: "standing-data",
    opens: "/start/employees",
    setup: ["Öppna den befintliga vyn; spara eller skicka inga ändringar"],
    scenario: "S5",
    visual: {
      variant: 0,
    },
    text: {
      sv: {
        alt: "Medarbetare i startguiden",
        shows:
          "Startguidens produktionsflöde visar organisationens befintliga medarbetare.",
        proves:
          "Hjälpbilden visar var dessa uppgifter granskas i den verkliga vyn. Inga ändringar har sparats.",
      },
      en: {
        alt: "Employees in the setup guide",
        shows:
          "The production onboarding flow shows the organization’s existing employees.",
        proves:
          "This help image shows where these details are reviewed in the actual view. No changes have been saved.",
      },
    },
    use: ["help"],
  },
  {
    id: "onboarding.unit-create",
    module: "Skapa enhet i startguiden",
    area: "standing-data",
    opens: "/start/service-areas",
    setup: ["Öppna den befintliga vyn; spara eller skicka inga ändringar"],
    scenario: "S5",
    visual: {
      variant: 0,
    },
    text: {
      sv: {
        alt: "Osparat formulär för enhetens namn och depåadress i startguiden",
        shows:
          "Det osparade enhetsformuläret visar namn, depåadress och geografiskt sammanhang.",
        proves:
          "Hjälpbilden visar vilka uppgifter som granskas innan en enhet skapas. Ingen enhet har skapats.",
      },
      en: {
        alt: "Unsaved unit name and depot address form in the setup guide",
        shows:
          "The unsaved unit form shows its name, depot address and geographic context.",
        proves:
          "This help image shows the fields reviewed before creating a unit. No unit has been created.",
      },
    },
    use: ["help"],
  },
  {
    id: "standing-data.employee-advanced-overview",
    module: "Medarbetarens avancerade uppgifter",
    area: "standing-data",
    opens: "/resources/employees/:id?tab=advanced",
    setup: ["Öppna den befintliga vyn; spara eller skicka inga ändringar"],
    scenario: "S5",
    visual: {
      variant: 0,
    },
    text: {
      sv: {
        alt: "Medarbetarens avancerade uppgifter",
        shows:
          "Läsbilden samlar anställning, arbetspass, planeringsregler och kostnader.",
        proves:
          "Hjälpbilden visar var dessa uppgifter granskas i den verkliga vyn. Inga ändringar har sparats.",
      },
      en: {
        alt: "Employee advanced details",
        shows:
          "The read-only view brings together employment, shifts, planning rules and costs.",
        proves:
          "This help image shows where these details are reviewed in the actual view. No changes have been saved.",
      },
    },
    use: ["help"],
  },
  {
    id: "standing-data.unit-districts",
    module: "Enhetens distrikt",
    area: "standing-data",
    opens: "/resources/service-areas/:id?tab=districts",
    setup: ["Öppna den befintliga vyn; spara eller skicka inga ändringar"],
    scenario: "S5",
    visual: {
      variant: 0,
    },
    text: {
      sv: {
        alt: "Enhetens distrikt",
        shows:
          "Enhetens distriktsvy visar indelningen i sitt geografiska och resursmässiga sammanhang.",
        proves:
          "Hjälpbilden visar var dessa uppgifter granskas i den verkliga vyn. Inga ändringar har sparats.",
      },
      en: {
        alt: "Unit districts",
        shows:
          "The unit district view presents its divisions in their geographic and resource context.",
        proves:
          "This help image shows where these details are reviewed in the actual view. No changes have been saved.",
      },
    },
    use: ["help"],
  },
  {
    id: "standing-data.unit-shift-templates",
    module: "Enhetens arbetspassmallar",
    area: "standing-data",
    opens: "/resources/service-areas/:id?tab=shift-templates",
    setup: ["Öppna den befintliga vyn; spara eller skicka inga ändringar"],
    scenario: "S5",
    visual: {
      variant: 0,
    },
    text: {
      sv: {
        alt: "Enhetens arbetspassmallar",
        shows:
          "Vyn visar arbetspassmallarna som gäller för enheten och deras lokala eller ärvda värden.",
        proves:
          "Hjälpbilden visar var dessa uppgifter granskas i den verkliga vyn. Inga ändringar har sparats.",
      },
      en: {
        alt: "Unit shift templates",
        shows:
          "The view shows the unit’s applicable shift templates and their local or inherited values.",
        proves:
          "This help image shows where these details are reviewed in the actual view. No changes have been saved.",
      },
    },
    use: ["help"],
  },
  {
    id: "standing-data.unit-standing-shifts",
    module: "Enhetens ordinarie arbetspass",
    area: "standing-data",
    opens: "/resources/service-areas/:id?tab=shifts",
    setup: ["Öppna den befintliga vyn; spara eller skicka inga ändringar"],
    scenario: "S5",
    visual: {
      variant: 0,
    },
    text: {
      sv: {
        alt: "Enhetens ordinarie arbetspass",
        shows: "Enhetens ordinarie arbetspass visas i en resurstabell.",
        proves:
          "Hjälpbilden visar var dessa uppgifter granskas i den verkliga vyn. Inga ändringar har sparats.",
      },
      en: {
        alt: "Unit standing roster shifts",
        shows: "The unit’s standing roster shifts appear in a resource table.",
        proves:
          "This help image shows where these details are reviewed in the actual view. No changes have been saved.",
      },
    },
    use: ["help"],
  },
  {
    id: "standing-data.unit-care-plan-catalogue",
    module: "Enhetens genomförandeplaner",
    area: "standing-data",
    opens: "/resources/service-areas/:id?tab=care-plan",
    setup: ["Öppna den befintliga vyn; spara eller skicka inga ändringar"],
    scenario: "S5",
    visual: {
      variant: 0,
    },
    text: {
      sv: {
        alt: "Enhetens genomförandeplaner",
        shows:
          "Enhetens befintliga besöksmallar visas i sin gemensamma katalog.",
        proves:
          "Hjälpbilden visar var dessa uppgifter granskas i den verkliga vyn. Inga ändringar har sparats.",
      },
      en: {
        alt: "Unit care-plan catalogue",
        shows:
          "The unit’s existing visit templates appear in their shared catalogue.",
        proves:
          "This help image shows where these details are reviewed in the actual view. No changes have been saved.",
      },
    },
    use: ["help"],
  },
  {
    id: "standing-data.unit-edit-districts",
    module: "Granska enhetens distriktsinställningar",
    area: "standing-data",
    opens: "/resources/service-areas/:id/edit?section=districts",
    setup: ["Öppna den befintliga vyn; spara eller skicka inga ändringar"],
    scenario: "S5",
    visual: {
      variant: 0,
    },
    text: {
      sv: {
        alt: "Granska enhetens distriktsinställningar",
        shows:
          "Distriktsdelen av enhetsformuläret visas utan att uppgifter sparas.",
        proves:
          "Hjälpbilden visar var dessa uppgifter granskas i den verkliga vyn. Inga ändringar har sparats.",
      },
      en: {
        alt: "Review unit district settings",
        shows:
          "The district section of the unit form is shown without saving any fields.",
        proves:
          "This help image shows where these details are reviewed in the actual view. No changes have been saved.",
      },
    },
    use: ["help"],
  },
  {
    id: "standing-data.unit-edit-advanced",
    module: "Granska enhetens avancerade uppgifter",
    area: "standing-data",
    opens: "/resources/service-areas/:id/edit?section=advanced",
    setup: ["Öppna den befintliga vyn; spara eller skicka inga ändringar"],
    scenario: "S5",
    visual: {
      variant: 0,
    },
    text: {
      sv: {
        alt: "Granska enhetens avancerade uppgifter",
        shows:
          "Enhetens avancerade formulärdel visar var lokala uppgifter kan granskas.",
        proves:
          "Hjälpbilden visar var dessa uppgifter granskas i den verkliga vyn. Inga ändringar har sparats.",
      },
      en: {
        alt: "Review unit advanced details",
        shows:
          "The unit’s advanced form section shows where local details can be reviewed.",
        proves:
          "This help image shows where these details are reviewed in the actual view. No changes have been saved.",
      },
    },
    use: ["help"],
  },
  {
    id: "standing-data.unit-edit-optimization",
    module: "Granska enhetens optimeringsinställningar",
    area: "standing-data",
    opens: "/resources/service-areas/:id/edit?section=optimization",
    setup: ["Öppna den befintliga vyn; spara eller skicka inga ändringar"],
    scenario: "S5",
    visual: {
      variant: 0,
    },
    text: {
      sv: {
        alt: "Granska enhetens optimeringsinställningar",
        shows:
          "Optimeringsdelen visar enhetens nuvarande planeringsinställningar och ärvda standarder.",
        proves:
          "Hjälpbilden visar var dessa uppgifter granskas i den verkliga vyn. Inga ändringar har sparats.",
      },
      en: {
        alt: "Review unit optimization settings",
        shows:
          "The optimization section shows the unit’s current planning settings and inherited defaults.",
        proves:
          "This help image shows where these details are reviewed in the actual view. No changes have been saved.",
      },
    },
    use: ["help"],
  },
  {
    id: "standing-data.employee-role-catalogue",
    module: "Organisationens medarbetarroller",
    area: "standing-data",
    opens: "/settings?tab=employeeRoles",
    setup: ["Öppna den befintliga vyn; spara eller skicka inga ändringar"],
    scenario: "S5",
    visual: {
      variant: 0,
    },
    text: {
      sv: {
        alt: "Organisationens medarbetarroller",
        shows:
          "Rollkatalogen visar organisationens befintliga medarbetarroller.",
        proves:
          "Hjälpbilden visar var dessa uppgifter granskas i den verkliga vyn. Inga ändringar har sparats.",
      },
      en: {
        alt: "Organization employee-role catalogue",
        shows:
          "The role catalogue shows the organization’s existing employee roles.",
        proves:
          "This help image shows where these details are reviewed in the actual view. No changes have been saved.",
      },
    },
    use: ["help"],
  },
  {
    id: "standing-data.mcp-connect-guide",
    module: "Anslut en AI-klient till CAIRE",
    area: "standing-data",
    opens: "/settings?tab=integrations",
    setup: ["Öppna den befintliga vyn; spara eller skicka inga ändringar"],
    scenario: "S5",
    visual: {
      variant: 0,
    },
    text: {
      sv: {
        alt: "Anslut en AI-klient till CAIRE",
        shows:
          "Den inbyggda guiden visar hur en AI-klient ansluts med CAIRE-inloggning.",
        proves:
          "Hjälpbilden visar var dessa uppgifter granskas i den verkliga vyn. Inga ändringar har sparats.",
      },
      en: {
        alt: "Connect an AI client to CAIRE",
        shows:
          "The built-in guide explains how an AI client connects using CAIRE sign-in.",
        proves:
          "This help image shows where these details are reviewed in the actual view. No changes have been saved.",
      },
    },
    use: ["help"],
  },
];
